import { AloftPrice } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectADUCost, selectADUCosts } from './selectADUCost.ts';
import { selectAtticCost, selectAtticCosts } from './selectAtticCost.ts';
import { selectBarnCost, selectBarnCosts } from './selectBarnCost.ts';
import {
  selectBelowGradeFullBathCost,
  selectBelowGradeFullBathCosts,
} from './selectBelowGradeFullBathCost.ts';
import {
  selectBelowGradeHalfBathCost,
  selectBelowGradeHalfBathCosts,
} from './selectBelowGradeHalfBathCost.ts';
import { selectCarportCost, selectCarportCosts } from './selectCarportCost.ts';
import { selectDeckCost, selectDeckCosts } from './selectDeckCost.ts';
import {
  selectFinishedBasementAreaCost,
  selectFinishedBasementAreaCosts,
} from './selectFinishedBasementAreaCost.ts';
import { selectFireplaceCost, selectFireplaceCosts } from './selectFireplaceCost.ts';
import { selectFullBathCost, selectFullBathCosts } from './selectFullBathCost.ts';
import { selectGarageCost, selectGarageCosts } from './selectGarageCost.ts';
import { selectHalfBathCost, selectHalfBathCosts } from './selectHalfBathCost.ts';
import { selectMainStructureCost, selectMainStructureCosts } from './selectMainStructureCost.ts';
import { selectManualCost, selectManualCosts } from './selectManualCost.ts';
import { selectPatioCost, selectPatioCosts } from './selectPatioCost.ts';
import { selectPoolCost, selectPoolCosts } from './selectPoolCost.ts';
import { selectPorchCost, selectPorchCosts } from './selectPorchCost.ts';
import { selectShedCost, selectShedCosts } from './selectShedCost.ts';
import { selectSpaCost, selectSpaCosts } from './selectSpaCost.ts';
import {
  selectTotalBasementAreaCost,
  selectTotalBasementAreaCosts,
} from './selectTotalBasementAreaCost.ts';
import { selectWorkshopCost, selectWorkshopCosts } from './selectWorkshopCost.ts';

export type CostNewByCategory = {
  gla: AloftPrice;
  garage: AloftPrice;
  carport: AloftPrice;
  basementTotal: AloftPrice;
  basementFinished: AloftPrice;
  attic: AloftPrice;
  fireplace: AloftPrice;
  fullBath: AloftPrice;
  halfBath: AloftPrice;
  belowGradeFullBath: AloftPrice;
  belowGradeHalfBath: AloftPrice;
  shed: AloftPrice;
  workshop: AloftPrice;
  barn: AloftPrice;
  pool: AloftPrice;
  spa: AloftPrice;
  adu: AloftPrice;
  porch: AloftPrice;
  patio: AloftPrice;
  deck: AloftPrice;
  manualCost: AloftPrice;
};

const calculateCostNewByCategory = (
  gla: ReturnType<typeof selectMainStructureCost> | undefined,
  garage: ReturnType<typeof selectGarageCost> | undefined,
  carport: ReturnType<typeof selectCarportCost> | undefined,
  basementTotal: ReturnType<typeof selectTotalBasementAreaCost> | undefined,
  basementFinished: ReturnType<typeof selectFinishedBasementAreaCost> | undefined,
  attic: ReturnType<typeof selectAtticCost> | undefined,
  fireplace: ReturnType<typeof selectFireplaceCost> | undefined,
  fullBath: ReturnType<typeof selectFullBathCost> | undefined,
  halfBath: ReturnType<typeof selectHalfBathCost> | undefined,
  belowGradeFullBath: ReturnType<typeof selectBelowGradeFullBathCost> | undefined,
  belowGradeHalfBath: ReturnType<typeof selectBelowGradeHalfBathCost> | undefined,
  shed: ReturnType<typeof selectShedCost> | undefined,
  workshop: ReturnType<typeof selectWorkshopCost> | undefined,
  barn: ReturnType<typeof selectBarnCost> | undefined,
  pool: ReturnType<typeof selectPoolCost> | undefined,
  spa: ReturnType<typeof selectSpaCost> | undefined,
  adu: ReturnType<typeof selectADUCost> | undefined,
  porch: ReturnType<typeof selectPorchCost> | undefined,
  patio: ReturnType<typeof selectPatioCost> | undefined,
  deck: ReturnType<typeof selectDeckCost> | undefined,
  manualCost: ReturnType<typeof selectManualCost> | undefined,
): CostNewByCategory => {
  return {
    gla: gla ?? AloftPrice.zero,
    garage: garage ?? AloftPrice.zero,
    carport: carport ?? AloftPrice.zero,
    basementTotal: basementTotal ?? AloftPrice.zero,
    basementFinished: basementFinished ?? AloftPrice.zero,
    attic: attic ?? AloftPrice.zero,
    fireplace: fireplace ?? AloftPrice.zero,
    fullBath: fullBath ?? AloftPrice.zero,
    halfBath: halfBath ?? AloftPrice.zero,
    belowGradeFullBath: belowGradeFullBath ?? AloftPrice.zero,
    belowGradeHalfBath: belowGradeHalfBath ?? AloftPrice.zero,
    shed: shed ?? AloftPrice.zero,
    workshop: workshop ?? AloftPrice.zero,
    barn: barn ?? AloftPrice.zero,
    pool: pool ?? AloftPrice.zero,
    spa: spa ?? AloftPrice.zero,
    adu: adu ?? AloftPrice.zero,
    porch: porch ?? AloftPrice.zero,
    patio: patio ?? AloftPrice.zero,
    deck: deck ?? AloftPrice.zero,
    manualCost: manualCost ?? AloftPrice.zero,
  };
};

export const selectCostNewByCategory = createSelector(
  [
    selectMainStructureCost,
    selectGarageCost,
    selectCarportCost,
    selectTotalBasementAreaCost,
    selectFinishedBasementAreaCost,
    selectAtticCost,
    selectFireplaceCost,
    selectFullBathCost,
    selectHalfBathCost,
    selectBelowGradeFullBathCost,
    selectBelowGradeHalfBathCost,
    selectShedCost,
    selectWorkshopCost,
    selectBarnCost,
    selectPoolCost,
    selectSpaCost,
    selectADUCost,
    selectPorchCost,
    selectPatioCost,
    selectDeckCost,
    selectManualCost,
  ],
  calculateCostNewByCategory,
);

export const selectCostNewByCategories = createSelector(
  [
    selectMainStructureCosts,
    selectGarageCosts,
    selectCarportCosts,
    selectTotalBasementAreaCosts,
    selectFinishedBasementAreaCosts,
    selectAtticCosts,
    selectFireplaceCosts,
    selectFullBathCosts,
    selectHalfBathCosts,
    selectBelowGradeFullBathCosts,
    selectBelowGradeHalfBathCosts,
    selectShedCosts,
    selectWorkshopCosts,
    selectBarnCosts,
    selectPoolCosts,
    selectSpaCosts,
    selectADUCosts,
    selectPorchCosts,
    selectPatioCosts,
    selectDeckCosts,
    selectManualCosts,
    selectCompSequence,
  ],
  (
    glas,
    garages,
    carports,
    basementTotals,
    basementFinisheds,
    attics,
    fireplaces,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    sheds,
    workshops,
    barns,
    pools,
    spas,
    adus,
    porches,
    patios,
    decks,
    manualCosts,
    compSequence,
  ) => {
    const properties = ['subject', ...compSequence];

    return properties.reduce((acc: Record<string, CostNewByCategory>, propID) => {
      acc[propID] = calculateCostNewByCategory(
        glas[propID],
        garages[propID],
        carports[propID],
        basementTotals[propID],
        basementFinisheds[propID],
        attics[propID],
        fireplaces[propID],
        fullBaths[propID],
        halfBaths[propID],
        belowGradeFullBaths[propID],
        belowGradeHalfBaths[propID],
        sheds[propID],
        workshops[propID],
        barns[propID],
        pools[propID],
        spas[propID],
        adus[propID],
        porches[propID],
        patios[propID],
        decks[propID],
        manualCosts[propID],
      );
      return acc;
    }, {});
  },
);
